import {
  User,
  CreditCard,
  Bell,
  GraduationCap,
  Palette,
  LogOut,
  Bug,
} from 'lucide-react';

/**
 * Settings surfaces Personal (which also houses change password +
 * delete account), Payment, Notifications, and Learning Style (which
 * now embeds the Lesson Flows controls). The old standalone Account
 * section is kept as a file but hidden - its contents live inside
 * Personal now.
 */
export type SettingsSection =
  | 'personal'
  | 'payment'
  | 'notifications'
  | 'learning-style'
  | 'appearance'
  | 'report';

export const SETTINGS_SECTIONS = [
  { key: 'personal' as const, label: 'Account', icon: User },
  { key: 'payment' as const, label: 'Payment', icon: CreditCard },
  { key: 'notifications' as const, label: 'Notifications', icon: Bell },
  {
    key: 'learning-style' as const,
    label: 'Learning style',
    icon: GraduationCap,
  },
  { key: 'appearance' as const, label: 'Appearance', icon: Palette },
];

interface SettingsNavProps {
  activeSection: SettingsSection;
  onSectionChange: (section: SettingsSection) => void;
  onSignOut: () => void;
  isSigningOut?: boolean;
}

export function SettingsNav({
  activeSection,
  onSectionChange,
  onSignOut,
  isSigningOut = false,
}: SettingsNavProps) {
  const isReportActive = activeSection === 'report';
  return (
    // Named to stay distinguishable from the app's 'Main' nav landmark in
    // `BrandBar`; see there for why.
    <nav aria-label='Settings sections' className='flex h-full flex-col p-3'>
      <div className='flex flex-col gap-1'>
        {SETTINGS_SECTIONS.map(({ key, label, icon: Icon }) => {
          const isActive = activeSection === key;
          return (
            <button
              key={key}
              data-testid={`settings-tab-${key}`}
              onClick={() => onSectionChange(key)}
              // The active section was a colour and nothing else, so a screen
              // reader could not tell which panel was open.
              aria-current={isActive ? 'page' : undefined}
              className={`flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-accent'
              }`}
            >
              <Icon className='h-4 w-4' />
              {label}
            </button>
          );
        })}
      </div>

      <div className='mt-auto flex flex-col gap-1 border-t border-border/60 pt-3'>
        <button
          onClick={() => onSectionChange('report')}
          aria-current={isReportActive ? 'page' : undefined}
          className={`flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
            isReportActive
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-accent'
          }`}
        >
          <Bug className='h-4 w-4' />
          Report a problem
        </button>
        {/* Sign-out uses the same blush-red palette as the destructive
            toast (#FEF2F2 / #FECACA / #991B1B mapped to red-50 / red-200 /
            red-800) so destructive surfaces read consistently across the
            app instead of mixing saturated and soft reds. */}
        <button
          onClick={onSignOut}
          disabled={isSigningOut}
          className='flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium text-red-800 transition-all duration-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-950/30'
        >
          <LogOut className='h-4 w-4' />
          {isSigningOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </nav>
  );
}
